import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "./util";

export type ButtonVariant =
  | "primary" // amber utility chip — the everyday tool button
  | "submit" // signal orange — if it advances you, it is orange
  | "secondary" // carbon slab — right-rail
  | "danger" // racetrack red — destructive only
  | "arrow"; // round forward affordance

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Stretch to the container. Mobile default for anything full-width. */
  block?: boolean;
  /** Slow amber breathe: marks the one thing the player should do next. */
  beacon?: boolean;
  children?: ReactNode;
}

/**
 * Always a real `<button>`. `.chip` supplies the raised bevel and the
 * translateY press state from chrome.css, so every variant feels stamped.
 */
export function Button({
  variant = "primary",
  block = false,
  beacon = false,
  className,
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        "btn",
        `btn-${variant}`,
        "chip",
        block && "bd-block",
        beacon && !rest.disabled && "bd-beacon",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Circle-arrow "go". Roundness is rationed — this is one of the few uses. */
export function ArrowButton({
  label,
  className,
  ...rest
}: Omit<ButtonProps, "variant" | "children"> & { label: string }) {
  return (
    <Button variant="arrow" aria-label={label} className={className} {...rest}>
      <span aria-hidden="true">&#9654;</span>
    </Button>
  );
}
